// ============================================================================
// JARVISTRAVEL - SIGN UP SCREEN (3-Step Flow)
// Version: 3.0.0 — Clerk useSignUp() hook (JAR-393)
// ============================================================================

import React, { useState } from 'react';
import { useSignUp } from '@clerk/clerk-react';
import { clerkErrorMessage, clerkFieldError } from '../../utils/clerkError';
import { usePasswordRules, checkPassword, describePasswordRules } from './passwordRules';
import { ChevronLeft, Check, Mail, Lock, Phone, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { Button, Input, OfflineNotice, toast } from '@/app/components/shared';
import { validateEmail, validateName } from '@/app/utils';
import { DEV_TEST_EMAIL } from '@/app/utils/devEmail';
import { NOT_A_PHONE, toE164 } from '@/app/utils/phone';
import { MARKETING_URLS } from '@/app/constants';

interface SignUpData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone: string;
  agreeToTerms: boolean;
  agreeToMarketing: boolean;
}

interface SignUpScreenProps {
  onSignUp: (data: SignUpData) => void;
  onSignIn: () => void;
}

const initialData: SignUpData = {
  email: DEV_TEST_EMAIL, password: '', firstName: '', lastName: '', phone: '', agreeToTerms: false, agreeToMarketing: false,
};

export function SignUpScreen({ onSignUp, onSignIn }: SignUpScreenProps) {
  const [step, setStep] = useState(1);
  const [data, setData] = useState<SignUpData>(initialData);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<Partial<Record<keyof SignUpData | 'confirmPassword', string>>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isLoaded, signUp } = useSignUp();
  // Clerk's own rules for this instance, so a password this form accepts is one
  // Clerk accepts (JAR-1702). Null until read, and null if it cannot be.
  const passwordRules = usePasswordRules();
  
  const updateField = <K extends keyof SignUpData>(field: K, value: SignUpData[K]) => {
    setData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validateStep1 = (): boolean => {
    const newErrors: typeof errors = {};
    if (!data.email) newErrors.email = 'Email is required';
    else if (!validateEmail(data.email)) newErrors.email = 'Invalid email address';
    if (!data.password) newErrors.password = 'Password is required';
    else {
      const broken = checkPassword(data.password, passwordRules);
      if (broken) newErrors.password = broken;
    }
    if (!confirmPassword) newErrors.confirmPassword = 'Please confirm your password';
    else if (data.password !== confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = (): boolean => {
    const newErrors: typeof errors = {};
    if (!data.firstName || !validateName(data.firstName)) newErrors.firstName = 'First name is required (2-50 characters)';
    // Optional, but a number that can't be saved is said, not dropped (Brent, JAR-2619).
    if (data.phone.trim() && !toE164(data.phone)) newErrors.phone = NOT_A_PHONE;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = (): boolean => {
    const newErrors: typeof errors = {};
    if (!data.agreeToTerms) newErrors.agreeToTerms = 'You must agree to the terms';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) setStep(3);
  };
  const handleBack = () => { if (step > 1) setStep(step - 1); };

  const handleSubmit = async () => {
    if (!validateStep3() || !isLoaded) return;
    setIsSubmitting(true);
    // In E.164, the form core stores. Step 2 has refused a number that can't be
    // one, so this is a valid number or nothing.
    const phone = toE164(data.phone);
    try {
      const result = await signUp.create({
        emailAddress: data.email,
        password: data.password,
        firstName: data.firstName,
        lastName: data.lastName,
        // The production instance has legal consent switched on, so Clerk
        // refuses a create without it. `validateStep3` has already required the
        // checkbox, and the value is sent rather than a literal `true` so the
        // consent Clerk records is the one the person actually gave.
        legalAccepted: data.agreeToTerms,
        // The marketing choice travels with the account: Clerk keeps it on the
        // user, and the user.created webhook reads it to decide whether this
        // person gets marketing email (JAR-1695). Unticked is sent as a no.
        // The phone travels the same way, for the webhook to store (JAR-2619).
        unsafeMetadata: { marketing_emails: data.agreeToMarketing, ...(phone && { phone }) },
      });

      // Prepare email verification (Clerk sends the code)
      if (result.status === 'missing_requirements') {
        await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
      }

      // Clerk manages the session — parent picks up user via useUser().
      // Notify parent for analytics + navigation
      onSignUp(data);
    } catch (err) {
      // A refused PASSWORD goes back to the step it was typed on and lands under
      // that field (JAR-1702). It used to be a toast here on step 3, about a field
      // two steps back that this screen does not show, so the person had to work
      // out which field was wrong. Strength and breach refusals only ever come
      // from here: the form cannot judge those locally, only Clerk can. The
      // confirmation is cleared because it matched the password that was refused.
      const passwordRefusal = clerkFieldError(err, 'password');
      if (passwordRefusal) {
        setConfirmPassword('');
        setErrors({ password: passwordRefusal });
        setStep(1);
        return;
      }
      toast.error(
        clerkErrorMessage(err, 'Registration failed', 'Couldn\'t create your account. Please try again.'),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const steps = [{ num: 1 }, { num: 2 }, { num: 3 }];
  const pwToggle = (
    <button type="button" onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? 'Hide password' : 'Show password'} className="text-muted hover:text-body">
      {showPassword ? <EyeOff className="w-5 h-5" strokeWidth={1.75} /> : <Eye className="w-5 h-5" strokeWidth={1.75} />}
    </button>
  );

  return (
    <div className="min-h-[100dvh] bg-bg flex flex-col">
      {/* Header */}
      <div className="pt-8 pb-4 px-6">
        <div className="flex items-center justify-between mb-6">
          <button onClick={step === 1 ? onSignIn : handleBack} aria-label="Back" className="p-2 -ml-2 text-muted hover:text-ink">
            <ChevronLeft className="w-6 h-6" strokeWidth={2} />
          </button>
          <div className="text-center">
            <h1 className="text-xl font-bold text-ink">Create Account</h1>
            <p className="text-sm text-muted">Step {step} of 3</p>
          </div>
          <div className="w-10" />
        </div>
        <div className="flex items-center justify-center gap-2 mb-4">
          {steps.map((s, i) => (
            <React.Fragment key={s.num}>
              <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold transition-colors ${step >= s.num ? 'bg-brand text-brand-fg' : 'bg-surface-raised text-muted'}`}>
                {step > s.num ? <Check className="w-4 h-4" strokeWidth={3} /> : s.num}
              </div>
              {i < steps.length - 1 && <div className={`w-12 h-1 rounded-full transition-colors ${step > s.num ? 'bg-brand' : 'bg-surface-raised'}`} />}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Form content */}
      <div className="flex-1 px-6 pb-8">
        <div className="bg-surface border border-border rounded-3xl shadow-xl shadow-black/5 p-6 max-w-md mx-auto">
          {step === 1 && (
            <div className="space-y-5">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-ink">Account details</h2>
                <p className="text-muted mt-1">Set up your login credentials</p>
              </div>
              <Input type="email" label="Email Address" placeholder="your@email.com" value={data.email} onChange={(e) => updateField('email', e.target.value)} error={errors.email} leftIcon={<Mail className="w-5 h-5" strokeWidth={1.75} />} />
              <Input type={showPassword ? 'text' : 'password'} label="Password" placeholder="Create a strong password" value={data.password} onChange={(e) => updateField('password', e.target.value)} error={errors.password} helperText={describePasswordRules(passwordRules)} leftIcon={<Lock className="w-5 h-5" strokeWidth={1.75} />} rightIcon={pwToggle} />
              <Input type={showPassword ? 'text' : 'password'} label="Confirm Password" placeholder="Confirm your password" value={confirmPassword} onChange={(e) => { setConfirmPassword(e.target.value); setErrors((prev) => ({ ...prev, confirmPassword: undefined })); }} error={errors.confirmPassword} leftIcon={<Lock className="w-5 h-5" strokeWidth={1.75} />} />
              <Button onClick={handleNext} variant="primary" fullWidth size="lg">Continue</Button>
              {/* No social sign-in row until a provider is enabled in Clerk and wired to
                  its OAuth flow (JAR-1406). The buttons had no handler, and the instance
                  has no social connections, so all four did nothing. */}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-ink">Personal details</h2>
                <p className="text-muted mt-1">Tell us a bit about yourself</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input label="First Name" placeholder="John" value={data.firstName} onChange={(e) => updateField('firstName', e.target.value)} error={errors.firstName} />
                <Input label="Last Name" placeholder="Doe" value={data.lastName} onChange={(e) => updateField('lastName', e.target.value)} />
              </div>
              <Input type="tel" label="Phone Number (Optional)" placeholder="+1 (555) 000-0000" value={data.phone} onChange={(e) => updateField('phone', e.target.value)} error={errors.phone} leftIcon={<Phone className="w-5 h-5" strokeWidth={1.75} />} />
              <Button onClick={handleNext} variant="primary" fullWidth size="lg">Continue</Button>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-ink">Almost there</h2>
                <p className="text-muted mt-1">Review and accept our terms</p>
              </div>
              <div className="bg-surface-raised rounded-2xl p-4">
                <h3 className="font-semibold text-ink mb-3">Terms summary</h3>
                <ul className="space-y-2 text-sm text-body">
                  {['Your data is encrypted in transit', 'Your data is shared only with the services that run JarvisTravel', 'Cancel anytime. No questions asked.'].map((t) => (
                    <li key={t} className="flex items-start gap-2">
                      <ShieldCheck className="w-5 h-5 text-success shrink-0 mt-0.5" strokeWidth={1.75} />{t}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="space-y-3">
                <label className={`flex items-start gap-3 cursor-pointer p-3 rounded-xl border transition-colors ${errors.agreeToTerms ? 'border-danger/40 bg-danger/5' : 'border-border hover:border-accent/40'}`}>
                  <input type="checkbox" checked={data.agreeToTerms} onChange={(e) => updateField('agreeToTerms', e.target.checked)} className="w-5 h-5 accent-brand rounded mt-0.5" />
                  <span className="text-sm text-body">
                    I agree to the{' '}
                    <a href={MARKETING_URLS.terms} target="_blank" rel="noreferrer" className="text-accent hover:underline">Terms of Service</a>
                    {' '}and{' '}
                    <a href={MARKETING_URLS.privacy} target="_blank" rel="noreferrer" className="text-accent hover:underline">Privacy Policy</a>
                  </span>
                </label>
                {errors.agreeToTerms && <p className="text-sm text-danger -mt-2 ml-8">{errors.agreeToTerms}</p>}
                <label className="flex items-start gap-3 cursor-pointer p-3 rounded-xl border border-border hover:border-accent/40 transition-colors">
                  <input type="checkbox" checked={data.agreeToMarketing} onChange={(e) => updateField('agreeToMarketing', e.target.checked)} className="w-5 h-5 accent-brand rounded mt-0.5" />
                  <span className="text-sm text-body">Email me travel tips, news from JarvisTravel and the occasional offer (optional)</span>
                </label>
              </div>
              {/* Inline while unreachable — submit stays enabled; a submit IS
                  the retry (JAR-314). */}
              <OfflineNotice />
              {/* Bot protection is on for the production instance (Turnstile,
                  smart widget). Clerk mounts the challenge into this element
                  during signUp.create; without it the widget has nowhere to go
                  and a human sees a create that fails for no stated reason. It
                  renders on this step because this is the step that creates. */}
              <div id="clerk-captcha" className="empty:hidden" />
              <Button onClick={handleSubmit} variant="primary" fullWidth size="lg" loading={isSubmitting}>Create Account</Button>
            </div>
          )}
        </div>

        <p className="text-center mt-6 text-muted">
          Already have an account?{' '}
          <button onClick={onSignIn} className="text-accent font-semibold hover:opacity-80">Sign in</button>
        </p>
      </div>
    </div>
  );
}

export default SignUpScreen;
